import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { MENU_SIDEBAR, MENU_MEGA, MENU_ROOT } from '@/config/menu.config';
import { RoleHasPermission } from '@/types/auth.types';

const menuToModuleMap: Record<string, string> = {
  Dashboards: 'dashboard',
  'Roles & Permissions': 'role_permission_management',
  'User Management': 'user_management',
  'Organization Management': 'organization_management',
  'Boat Management': 'boat_management',
  'Harbor Management': 'harbor_management',
  'Community Management': 'community_management',
  'DMO Management': 'dmo_registration_management',
  'Berth Category Management': 'berth_category_management',
  'Promo Management': 'coupon_management',
  'Service Management': 'service_management',
  'Article Management': 'article_management',
  Region: 'region_management',
  'Yacht Clubs': 'club_management',
  'User Feedbacks/Reviews': 'feedback_management',
  'Audit Logs': 'activity_log',
  CMS: 'cms',
  Configuration: 'configuration_management',
  'External Services': 'third_party_integration',
  'Routing Management': 'routing_management',
  'Theme Routes': 'theme_route_management',
  'Icons Management': 'icons_management',
  'KPI Management': 'kpi_management',
  Deployements: 'deployment_management',
  'Subscription Management': 'subscription_management',
  'Service Provider Management': 'service_provider_management',
  'Service Moderation': 'service_moderation',
  'Email Template Management': 'email_template',
  'Notification Template Management': 'notification_template',
  'Notification Preferences': 'notification_rules',
  'Advertise Management': 'advertisement_management',
  'Product VAT Config': 'product_vat_config',
  'Penalty Configuration': 'subscription_management',
  'Revenue Report': 'subscription_management',
  Transactions: 'payment_management',
  'Subscription Transactions': 'invoice_management',
  Settlements: 'payment_management',
  'System Announcements': 'system_announcements',
  'Helpdesk FAQ': 'faq_management',
  'Platform Settings': 'platform_setting',
  'KYC Management': 'kyc_management',
  'Commission Policy': 'commission_policy_management',
  'PSP Fee Configuration': 'platform_setting',
  'Accounting Exports': 'netvisor_accounting_exports',
  'Subscription Trial Config': 'subscription_management',
  'Entitlement Grants': 'entitlement_grant_management',
  'List Value Management': 'list_value_management',
};

export const useFilteredMenu = () => {
  const userPermissions = useSelector(
    (state: RootState) => state.auth.userPermissions
  );
  const userData = useSelector((state: RootState) => state.auth.userData);
  const isPlatformAdmin = userData?.roles?.includes('admin');

  const allowedModules = useMemo(() => {
    const modules = new Set<string>();

    // Always add dashboard
    modules.add('dashboard');

    if (
      userPermissions &&
      Array.isArray(userPermissions) &&
      userPermissions.length > 0
    ) {
      userPermissions.forEach((permission: RoleHasPermission) => {
        if (permission.permissions?.module?.unique_key) {
          modules.add(permission.permissions.module.unique_key);
        }
      });
    }

    const isAdmin = userData?.roles?.includes('admin');

    if (isAdmin) {
      modules.add('role_permission_management');
    }

    return modules;
  }, [userPermissions, userData]);

  const filteredSidebar = useMemo(() => {
    const safeUserPermissions =
      userPermissions && Array.isArray(userPermissions) ? userPermissions : [];

    // Helper to check specific permission unique_key
    const hasPermission = (permKey: string) => {
      return safeUserPermissions.some(
        (p: RoleHasPermission) => p.permissions?.unique_key === permKey
      );
    };

    const hasPermissionIncludes = (permKey: string) => {
      return safeUserPermissions.some((p: RoleHasPermission) =>
        p.permissions?.unique_key?.includes(permKey)
      );
    };

    // Build a filtered copy that can also prune children
    const filtered = MENU_SIDEBAR.map((menuItem) => {
      // Clone children with filtering rules when applicable
      let children = menuItem.children;
      if (children && Array.isArray(children)) {
        children = children.filter((child) => {
          // Only apply special rule for Email Template Management; keep other children unchanged
          if (child.title === 'Email Template Management') {
            return hasPermission('modify_email_template');
          }
          // Apply the same rule for Notification Template Management
          if (child.title === 'Notification Template Management') {
            return hasPermission('modify_notification_template');
          }
          if (child.title === 'Product VAT Config') {
            return allowedModules.has('product_vat_config');
          }
          if (child.title === 'Domestic VAT Config') {
            return allowedModules.has('product_vat_config');
          }
          if (child.title === 'Business Entity VAT Config') {
            return allowedModules.has('product_vat_config');
          }
          if (child.title === 'Penalty Configuration') {
            return hasPermission('subscription_management_create');
          }
          if (child.title === 'Subscription Trial Config') {
            return hasPermission('subscription_management_create');
          }
          if (child.title === 'Entitlement Grants') {
            return hasPermission('entitlement_grant_management_read');
          }
          if (child.title === 'Booking Reminder Configuration') {
            return hasPermission('booking_reminder_config');
          }
          if (child.title === 'Guest Visibility') {
            return hasPermission('guest_visibility_manage');
          }
          if (child.title === 'Guest Booking Governance') {
            // Per-harbor overrides now live on the harbor's own detail page;
            // this platform-wide page is admin-only regardless of who else
            // might hold the permission key via Role Permissions.
            return (
              isPlatformAdmin &&
              hasPermission('guest_booking_governance_manage')
            );
          }
          if (child.title === 'Commission Policy') {
            return (
              allowedModules.has('commission_policy_management') ||
              hasPermissionIncludes('commission_policy_management')
            );
          }
          if (child.title === 'PSP Fee Configuration') {
            return allowedModules.has('platform_setting');
          }
          if (child.title === 'Environment Variables') {
            return hasPermissionIncludes('external_service_read');
          }
          if (child.title === 'Synthetic Booking Canary') {
            return hasPermissionIncludes('synthetic_booking_canary_read');
          }
          return true;
        });
      }

      return { ...menuItem, children };
    }).filter((menuItem) => {
      const moduleKey = menuItem.title
        ? menuToModuleMap[menuItem.title]
        : undefined;

      // Special-case: show "Service Management" when user has either
      // service_management OR service_provider_management module access.
      if (menuItem.title === 'Service Management') {
        if (
          allowedModules.has('service_management') ||
          allowedModules.has('service_provider_management')
        ) {
          return true;
        }
        return false;
      }

      if (menuItem.title === 'Service Provider Management') {
        return hasPermission('service_provider_management_read');
      }

      // Special-case: show "Theme Routes" only when user has theme_route_management_read permission
      if (menuItem.title === 'Theme Routes') {
        return hasPermission('theme_route_management_read');
      }
      if (menuItem.title === 'KYC Management') {
        return hasPermission('kyc_management_change_status');
      }
      if (menuItem.title === 'Subscription Management') {
        return hasPermission('subscription_management_create');
      }
      if (menuItem.title === 'Configuration') {
        if (allowedModules.has('configuration_management')) {
          return true;
        }

        if (menuItem.children && menuItem.children.length > 0) {
          return true;
        }

        return false;
      }

      // For items with children: preserve original behavior -> include only if parent module is allowed
      if (menuItem.children) {
        return moduleKey ? allowedModules.has(moduleKey) : false;
      }

      if (moduleKey && allowedModules.has(moduleKey)) {
        return true;
      }

      return false;
    });

    return filtered as typeof MENU_SIDEBAR;
  }, [userPermissions, allowedModules, isPlatformAdmin]);

  return {
    filteredSidebar,
    menuMega: MENU_MEGA,
    menuRoot: MENU_ROOT,
  };
};
